import React, { useMemo } from "react";
import { RiStackLine } from "react-icons/ri";

export const LotteryBlockSetsPanel = ({
  currentBlocks = {},
  className = "",
}) => {
  const blockStats = useMemo(() => {
    const raw = currentBlocks || {};
    const entries = Object.entries(raw)
      .map(([sizeStr, setCount]) => {
        const size = Number(sizeStr);
        const sets = Number(setCount) || 0;
        const tickets = sets * size;
        return {
          size,
          sets,
          tickets,
        };
      })
      .filter((item) => !isNaN(item.size) && item.sets > 0);

    entries.sort((a, b) => a.size - b.size);

    const totalSets = entries.reduce((acc, curr) => acc + curr.sets, 0);
    const totalTickets = entries.reduce((acc, curr) => acc + curr.tickets, 0);

    const items = entries.map((item) => ({
      ...item,
      label: item.size === 1 ? "ใบเดี่ยว (1 ใบ)" : `ชุด ${item.size} ใบ`,
      percent:
        totalTickets > 0
          ? `${((item.tickets / totalTickets) * 100).toFixed(1)}%`
          : "0.0%",
    }));

    return {
      items,
      totalSets,
      totalTickets,
    };
  }, [currentBlocks]);

  return (
    <div
      className={`bg-white rounded-md p-3.5 shadow-sm border border-gray-200 flex flex-col justify-between ${className}`}
    >
      <div>
        {/* Panel Header */}
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-3 mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-600 shadow-sm shrink-0">
              <RiStackLine className="text-lg" />
            </div>
            <div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <h3 className="font-bold text-sm text-gray-900">
                  สรุปจำนวนเลขชุด
                </h3>
              </div>
              <p className="text-[11px] text-gray-500 font-medium">
                สลากพร้อมขายบนแผง
              </p>
            </div>
          </div>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 shrink-0">
            วางขาย
          </span>
        </div>

        {/* Total Summary Highlight Card */}
        <div className="bg-gradient-to-br from-[#1e293b] to-[#0f172a] text-white p-3 rounded-md shadow-xs border border-white/10 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
              สลากบนแผงทั้งหมด
            </span>
            <span className="text-[10px] font-extrabold text-white bg-white/20 border border-white/30 px-1.5 py-0.2 rounded-md font-mono">
              100%
            </span>
          </div>
          <p className="text-2xl font-black text-white mt-1 font-mono">
            {blockStats.totalTickets.toLocaleString()}
            <span className="text-xs font-normal text-slate-300 ml-1 font-sans">
              ใบ
            </span>
          </p>
          <p className="text-[11px] text-indigo-200 font-medium mt-0.5">
            รวม {blockStats.totalSets.toLocaleString()} ชุดในระบบ
          </p>
        </div>

        {/* Set Breakdown List */}
        <div>
          <div className="flex items-center justify-between text-[11px] font-bold text-gray-700 mb-2">
            <span>แยกตามขนาดชุด</span>
            <span className="text-[10px] font-medium text-gray-400">
              {blockStats.items.length} รูปแบบ
            </span>
          </div>

          {blockStats.items.length === 0 ? (
            <div className="text-center py-8 text-xs text-gray-400">
              ยังไม่มีข้อมูลเลขชุดในงวดนี้
            </div>
          ) : (
            <div className="space-y-2">
              {blockStats.items.map((item) => (
                <div
                  key={item.size}
                  className="bg-slate-50/70 border border-slate-200 rounded-md p-2.5 hover:bg-white hover:border-indigo-300 hover:shadow-xs transition"
                >
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="font-bold text-xs text-slate-800">
                      {item.label}
                    </span>
                    <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 font-mono">
                      {item.percent}
                    </span>
                  </div>

                  <div className="flex items-baseline justify-between mt-1">
                    <div className="flex items-baseline gap-1">
                      <span className="text-lg font-black font-mono text-gray-900">
                        {item.tickets.toLocaleString()}
                      </span>
                      <span className="text-[10px] font-semibold text-gray-500">
                        ใบ
                      </span>
                    </div>
                    <span className="text-[11px] text-slate-500 font-medium">
                      {item.sets.toLocaleString()} ชุด
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden mt-2">
                    <div
                      className="bg-indigo-600 h-full rounded-full transition-all duration-500"
                      style={{ width: item.percent }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
        <span>อัปเดตทุก 5 นาที</span>
        <span>คำนวณจาก Blocks</span>
      </div>
    </div>
  );
};

export default LotteryBlockSetsPanel;
